import React, { useCallback, useEffect, useState } from "react";
import { Typography, message, Row, Col, Button } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAgentProfile } from "../../hooks/useAgentProfile";
import { fetchAgentBookingById } from "../../services/agentPortalService";
import { Booking } from "../../types/booking";
import { AGENT_ROUTES } from "../../config/agentPortal";
import { getAgentBookingsReturnPath } from "../../utils/agentBookingsNavigation";
import { AgentBookingOverview } from "../../components/agent/AgentBookingOverview";
import { getBookingEffReference } from "../../utils/bookingUtils";
import { AgentBookingContactsCard } from "../../components/agent/AgentBookingContactsCard";
import { AgentProductsTable } from "../../components/agent/AgentProductsTable";
import { AgentCommercialInvoice } from "../../components/agent/AgentCommercialInvoice";
import { AgentBookingContainerCard } from "../../components/agent/AgentBookingContainerCard";

const { Title } = Typography;

const AgentBookingDetailPage: React.FC = () => {
  const { id } = useParams();
  const bookingId = Number(id);
  const navigate = useNavigate();
  const location = useLocation();
  const bookingsReturnPath = getAgentBookingsReturnPath(location.state);
  const { agentId, loading: profileLoading } = useAgentProfile();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!agentId || !Number.isFinite(bookingId)) return;
    setLoading(true);
    const { data, error } = await fetchAgentBookingById(agentId, bookingId);
    if (error || !data) {
      message.error("Booking not found");
      navigate(bookingsReturnPath);
      return;
    }
    setBooking(data);
    setLoading(false);
  }, [agentId, bookingId, navigate, bookingsReturnPath]);

  const refreshBooking = useCallback(async () => {
    if (!agentId || !Number.isFinite(bookingId)) return;
    const { data } = await fetchAgentBookingById(agentId, bookingId);
    if (data) setBooking(data);
  }, [agentId, bookingId]);

  useEffect(() => {
    if (!profileLoading) load();
  }, [profileLoading, load]);

  const company = booking
    ? Array.isArray(booking.company_info)
      ? booking.company_info[0]
      : booking.company_info
    : null;

  const supplier = booking
    ? Array.isArray(booking.supplier_info)
      ? booking.supplier_info[0]
      : booking.supplier_info
    : null;

  if (profileLoading || loading || !booking || !agentId) {
    return null;
  }

  return (
    <div>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(bookingsReturnPath)}
        style={{ paddingLeft: 0, marginBottom: 8 }}
      >
        Back to bookings
      </Button>
      <Title level={4} style={{ marginTop: 0, marginBottom: 12 }}>
        {getBookingEffReference(booking)}
        {booking.reference ? (
          <Typography.Text type="secondary" style={{ fontWeight: 400, marginLeft: 8 }}>
            {booking.reference}
          </Typography.Text>
        ) : null}
      </Title>

      <Row gutter={[12, 12]}>
        <Col xs={24} lg={14}>
          <AgentBookingOverview
            booking={booking}
            agentId={agentId}
            onUpdated={refreshBooking}
          />
          <div style={{ marginTop: 12 }}>
            <AgentBookingContainerCard
              booking={booking}
              agentId={agentId}
              onUpdated={refreshBooking}
            />
          </div>
        </Col>
        <Col xs={24} lg={10}>
          <AgentCommercialInvoice booking={booking} onUpdated={refreshBooking} />
          <div style={{ marginTop: 12 }}>
            <AgentBookingContactsCard
              company={company}
              supplier={supplier}
              agentId={agentId}
            />
          </div>
        </Col>
      </Row>

      <div style={{ marginTop: 12 }}>
        <AgentProductsTable booking={booking} onUpdated={refreshBooking} />
      </div>
    </div>
  );
};

export default AgentBookingDetailPage;
